// lib/user-management/route-catalog.ts
// Single source of route checkboxes for Staff Access settings.
// Keep in sync with operations layout NAV_ITEMS + ADMIN_ROUTES.

export type AdminRouteCatalogItem = {
  id: string;
  label: string;
  path: string;
  group: "sidebar" | "admin_routes";
};

/** Top-level grantable prefixes shown in the invite/edit checklist. */
export const ADMIN_ROUTE_CATALOG: readonly AdminRouteCatalogItem[] = [
  // Sidebar
  {
    id: "overview",
    label: "Overview",
    path: "/admin/operations",
    group: "sidebar",
  },
  {
    id: "this-week",
    label: "This Week",
    path: "/admin/operations/this-week",
    group: "sidebar",
  },
  {
    id: "mission-control",
    label: "Mission Control",
    path: "/admin/operations/mission-control",
    group: "sidebar",
  },
  {
    id: "brain",
    label: "Brain",
    path: "/admin/operations/brain",
    group: "sidebar",
  },
  {
    id: "brain-calls",
    label: "Brain · Calls",
    path: "/admin/operations/brain/calls",
    group: "sidebar",
  },
  {
    id: "code-reviews",
    label: "Code Reviews",
    path: "/admin/operations/code-reviews",
    group: "sidebar",
  },
  {
    id: "goals",
    label: "Goals",
    path: "/admin/operations/goals",
    group: "sidebar",
  },
  {
    id: "tasks",
    label: "Tasks",
    path: "/admin/operations/tasks",
    group: "sidebar",
  },
  {
    id: "content",
    label: "Marketing",
    path: "/admin/operations/marketing",
    group: "sidebar",
  },
  {
    id: "gtm",
    label: "GTM",
    path: "/admin/operations/gtm",
    group: "sidebar",
  },
  {
    id: "client-roadmaps",
    label: "Client Roadmaps",
    path: "/admin/operations/client-roadmaps",
    group: "sidebar",
  },
  {
    id: "experiments",
    label: "Experiments",
    path: "/admin/operations/experiments",
    group: "sidebar",
  },
  {
    id: "crm",
    label: "CRM (all)",
    path: "/admin/operations/crm",
    group: "sidebar",
  },
  {
    id: "crm-companies",
    label: "CRM · Companies",
    path: "/admin/operations/crm/companies",
    group: "sidebar",
  },
  {
    id: "crm-people",
    label: "CRM · People",
    path: "/admin/operations/crm/people",
    group: "sidebar",
  },
  {
    id: "crm-deals",
    label: "CRM · Deals",
    path: "/admin/operations/crm/deals",
    group: "sidebar",
  },
  {
    id: "crm-lists",
    label: "CRM · Lists",
    path: "/admin/operations/crm/lists",
    group: "sidebar",
  },
  {
    id: "crm-activity",
    label: "CRM · Activity",
    path: "/admin/operations/crm/activity",
    group: "sidebar",
  },
  {
    id: "agent-console",
    label: "Agent Console",
    path: "/admin/operations/agent-console",
    group: "sidebar",
  },
  {
    id: "investments",
    label: "Investments",
    path: "/admin/operations/investments",
    group: "sidebar",
  },
  {
    id: "finance",
    label: "Finance",
    path: "/admin/operations/finance",
    group: "sidebar",
  },
  { id: "seo", label: "SEO", path: "/admin/operations/seo", group: "sidebar" },
  {
    id: "health",
    label: "Health",
    path: "/admin/operations/health",
    group: "sidebar",
  },
  { id: "me", label: "Me", path: "/admin/operations/me", group: "sidebar" },
  {
    id: "settings",
    label: "Settings (API tokens, staff access)",
    path: "/admin/operations/settings",
    group: "sidebar",
  },
  // Top-level admin routes
  {
    id: "outbound",
    label: "Outbound",
    path: "/admin/outbound-dashboard",
    group: "admin_routes",
  },
  { id: "news", label: "News", path: "/admin/news", group: "admin_routes" },
  {
    id: "templates",
    label: "Templates",
    path: "/admin/templates",
    group: "admin_routes",
  },
  {
    id: "sdr-brain",
    label: "SDR Brain",
    path: "/admin/sdr-brain",
    group: "admin_routes",
  },
  {
    id: "network",
    label: "Network",
    path: "/admin/network",
    group: "admin_routes",
  },
] as const;

/**
 * /admin/operations/content was renamed to /admin/operations/marketing
 * (2026-09-23). Grants and home paths saved before the rename still name the
 * old path in staff_admin_users, so they are rewritten on read: without this,
 * everyone granted Content would lose it the moment the rename deployed.
 */
export const LEGACY_CONTENT_PATH = "/admin/operations/content";
export const MARKETING_PATH = "/admin/operations/marketing";

/**
 * The SDR v1 agent face was retired in favour of GTM (2026-09-24). A grant or
 * home path that named the old agent page or the old SDR pipeline (or any page
 * under either) now means GTM. /api/admin/sdr/** normalizes to GTM too (see
 * normalizeAdminPathnameForAcl), because the GTM face is what calls it.
 */
export const LEGACY_SDR_AGENT_PATH = "/admin/operations/agents/sdr";
export const LEGACY_SDR_PATH = "/admin/operations/sdr";
export const GTM_PATH = "/admin/operations/gtm";

export function migrateLegacyAdminPath(path: string): string {
  if (path === LEGACY_CONTENT_PATH) return MARKETING_PATH;
  if (path.startsWith(`${LEGACY_CONTENT_PATH}/`)) {
    return MARKETING_PATH + path.slice(LEGACY_CONTENT_PATH.length);
  }
  for (const legacy of [LEGACY_SDR_AGENT_PATH, LEGACY_SDR_PATH]) {
    if (path === legacy || path.startsWith(`${legacy}/`)) return GTM_PATH;
  }
  return path;
}

export const STAFF_SETTINGS_PATH = "/admin/operations/settings";

/**
 * Synthetic ACL key for staff-access MANAGEMENT, deliberately distinct from the
 * page path above. It is never a real route.
 *
 * The settings page now hosts two things: staff access (super admin only) and
 * a person's own API tokens (any staff). Keying the super-admin gate on the
 * page path blocked the whole URL, which is why a content_ops user was
 * redirected away from the one place their own tokens live.
 */
export const STAFF_ACCESS_MANAGEMENT_PATH =
  "/admin/operations/settings/staff-access";
